import { Link, useRouter } from "@shared/client/navigation";
import { useState } from "react";
import { ArrowRight, CheckCircle2, Globe2, LoaderCircle, LockKeyhole, Mail } from "lucide-react";
import { EVENTS } from "@shared/lib/analytics";
import { track } from "@shared/lib/analytics-client";
import { requestPasswordReset, resetPassword, signIn, signInWithGoogle as startGoogle, signUp } from "@shared/lib/auth-client";
import { MIN_PASSWORD_LENGTH, safeNext } from "@shared/lib/auth-shared";
import { siteLabel } from "@shared/lib/url";
import { Alert, AlertDescription } from "@shared/components/ui/alert";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import { Separator } from "@shared/components/ui/separator";
import { GoogleIcon } from "@shared/components/brand-icons";
import { useT } from "@shared/client/locale";

type Mode = "login" | "signup" | "forgot" | "reset";

export function AuthForm({
  mode,
  initialError = "",
  resetToken = null,
  site,
}: {
  mode: Mode;
  initialError?: string;
  /** Jeton reçu par e-mail ; absent, le changement de mot de passe vise la session ouverte. */
  resetToken?: string | null;
  /**
   * Adresse saisie sur la landing (déjà validée par la page), reportée dans
   * l'URL de l'onboarding pour qu'il arrive prérempli.
   */
  site?: string;
}) {
  const router = useRouter();
  const t = useT();
  const form = t.auth.form;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [oauthBusy, setOauthBusy] = useState(false);
  const [error, setError] = useState(initialError);
  const [success, setSuccess] = useState("");
  const copy: { eyebrow?: string; title: string; description?: string; submit: string } = form.modes[mode];
  const needsEmail = mode !== "reset";
  const needsPassword = mode === "login" || mode === "signup" || mode === "reset";
  const onboardingPath = site ? `/onboarding?site=${encodeURIComponent(site)}` : "/onboarding";

  function signInWithGoogle() {
    setOauthBusy(true);
    setError("");
    setSuccess("");
    const requested = new URLSearchParams(window.location.search).get("next");
    startGoogle(mode === "signup" ? onboardingPath : safeNext(requested));
  }

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    setSuccess("");

    try {
      if (mode === "login") {
        const { onboardingCompleted } = await signIn(email, password);
        const requested = new URLSearchParams(window.location.search).get("next");
        router.replace(onboardingCompleted ? safeNext(requested) : "/onboarding");
        router.refresh();
        return;
      }

      if (mode === "signup") {
        if (password.length < MIN_PASSWORD_LENGTH) {
          throw new Error(form.passwordTooShort(MIN_PASSWORD_LENGTH));
        }
        await signUp(email, password);
        track(EVENTS.SIGNUP_COMPLETED);
        router.replace(onboardingPath);
        router.refresh();
        return;
      }

      if (mode === "forgot") {
        await requestPasswordReset(email);
        setSuccess(form.resetSent);
        return;
      }

      if (password.length < MIN_PASSWORD_LENGTH) {
        throw new Error(form.passwordTooShort(MIN_PASSWORD_LENGTH));
      }
      if (password !== confirmPassword) throw new Error(form.passwordsDiffer);
      await resetPassword(password, resetToken);
      setSuccess(form.passwordUpdated);
      window.setTimeout(() => {
        router.replace("/app");
        router.refresh();
      }, 900);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : form.failed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="w-full max-w-[420px]">
      <div className="mb-6">
        {copy.eyebrow && (
          <span className="text-[12px] font-medium text-muted-foreground">
            {copy.eyebrow}
          </span>
        )}
        <h1 className="mt-2 mb-2 font-serif text-[32px] leading-tight font-normal tracking-tight">{copy.title}</h1>
        {copy.description && (
          <p className="m-0 text-[13.5px] leading-relaxed text-muted-foreground">{copy.description}</p>
        )}
        {mode === "signup" && site && (
          <p className="m-0 flex items-center gap-2 text-[13.5px] leading-relaxed text-muted-foreground">
            <Globe2 aria-hidden="true" className="size-4 shrink-0" />
            <span>
              {form.firstAnalysisOn} <strong className="font-medium text-foreground">{siteLabel(site)}</strong>
            </span>
          </p>
        )}
      </div>

      {(mode === "signup" || mode === "login") && (
        <>
          <Button
            className="w-full"
            disabled={oauthBusy || busy}
            onClick={signInWithGoogle}
            size="lg"
            type="button"
            variant="outline"
          >
            {oauthBusy ? (
              <LoaderCircle className="animate-spin" />
            ) : (
              <GoogleIcon className="size-4" />
            )}
            <span>{form.google}</span>
          </Button>

          <div className="my-5 flex items-center gap-3">
            <Separator className="flex-1" />
            <span className="text-[12px] whitespace-nowrap text-muted-foreground">{form.orWithEmail}</span>
            <Separator className="flex-1" />
          </div>
        </>
      )}

      <form className="grid gap-4" onSubmit={submit}>
        {needsEmail && (
          <div className="grid gap-2">
            <Label htmlFor="auth-email">{form.email}</Label>
            <div className="relative">
              <Mail aria-hidden="true" className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                autoComplete="email"
                className="h-11 pl-9"
                id="auth-email"
                onChange={(event) => setEmail(event.target.value)}
                placeholder={form.emailPlaceholder}
                required
                type="email"
                value={email}
              />
            </div>
          </div>
        )}

        {needsPassword && (
          <div className="grid gap-2">
            <Label htmlFor="auth-password">{mode === "reset" ? form.newPassword : form.password}</Label>
            <div className="relative">
              <LockKeyhole aria-hidden="true" className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                className="h-11 pl-9"
                id="auth-password"
                minLength={8}
                onChange={(event) => setPassword(event.target.value)}
                required
                type="password"
                value={password}
              />
            </div>
          </div>
        )}

        {mode === "login" && (
          <Link className="justify-self-end text-[12px] font-semibold text-muted-foreground hover:text-foreground" href="/auth/forgot-password">
            {form.forgotLink}
          </Link>
        )}

        {mode === "reset" && (
          <div className="grid gap-2">
            <Label htmlFor="auth-confirm">{form.confirmPassword}</Label>
            <div className="relative">
              <LockKeyhole aria-hidden="true" className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                autoComplete="new-password"
                className="h-11 pl-9"
                id="auth-confirm"
                minLength={8}
                onChange={(event) => setConfirmPassword(event.target.value)}
                required
                type="password"
                value={confirmPassword}
              />
            </div>
          </div>
        )}

        <Button className="w-full" disabled={busy || Boolean(success)} size="lg" type="submit">
          {busy ? <LoaderCircle className="animate-spin" /> : <ArrowRight />}
          {busy ? form.busy : copy.submit}
        </Button>
      </form>

      {error && (
        <Alert className="mt-4" role="alert" variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert className="mt-4" role="status" variant="success">
          <CheckCircle2 />
          <AlertDescription className="text-success">{success}</AlertDescription>
        </Alert>
      )}

      <div className="mt-6 text-center text-[13px] text-muted-foreground">
        {mode === "login" && (
          <p className="m-0">
            {form.noAccount}{" "}
            <Link className="font-semibold text-primary hover:underline" href="/auth/signup">{form.signupLink}</Link>
          </p>
        )}
        {mode === "signup" && (
          <p className="m-0">
            {form.hasAccount}{" "}
            <Link className="font-semibold text-primary hover:underline" href="/auth/login">{form.loginLink}</Link>
          </p>
        )}
        {(mode === "forgot" || mode === "reset") && (
          <p className="m-0">
            <Link className="font-semibold text-primary hover:underline" href="/auth/login">{form.backToLogin}</Link>
          </p>
        )}
      </div>
    </div>
  );
}
